import { useEffect, useRef } from 'react';

interface UseBreakpointTransitionOptions {
    breakpoint: number;
    onEnterDesktop?: () => void;
    onEnterMobile?: () => void;
}

export const useBreakpointTransition = ({
    breakpoint = 744,
    onEnterDesktop,
    onEnterMobile,
}: UseBreakpointTransitionOptions) => {
    const isDesktopRef = useRef<boolean | null>(null);
    const hasTriggeredDesktopRef = useRef<boolean>(false);
    const hasTriggeredMobileRef = useRef<boolean>(false);

    useEffect(() => {
        const checkBreakpoint = () => {
            const isCurrentlyDesktop = window.innerWidth > breakpoint;

            if (isDesktopRef.current === null) {
                isDesktopRef.current = isCurrentlyDesktop;
                return;
            }

            if (isCurrentlyDesktop && !isDesktopRef.current && !hasTriggeredDesktopRef.current) {
                if (onEnterDesktop) {
                    onEnterDesktop();
                }
                hasTriggeredDesktopRef.current = true;
                hasTriggeredMobileRef.current = false;
            }

            if (!isCurrentlyDesktop && isDesktopRef.current && !hasTriggeredMobileRef.current) {
                if (onEnterMobile) {
                    onEnterMobile();
                }
                hasTriggeredMobileRef.current = true;
                hasTriggeredDesktopRef.current = false;
            }

            isDesktopRef.current = isCurrentlyDesktop;
        };

        checkBreakpoint();

        window.addEventListener('resize', checkBreakpoint);

        return () => {
            window.removeEventListener('resize', checkBreakpoint);
        };
    }, [breakpoint, onEnterDesktop, onEnterMobile]);

    const isDesktop = isDesktopRef.current ?? window.innerWidth > breakpoint;
    const isMobile = !isDesktop;

    return {
        isDesktop,
        isMobile,
        hasTriggeredDesktop: hasTriggeredDesktopRef.current,
        hasTriggeredMobile: hasTriggeredMobileRef.current,
    };
};
